import React, { useEffect, useState, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { URLS } from "../../../../_config";
import Layout from "../../../../components/layout";
import Header from "../../../../components/header";
import SubHeader from "../../../../components/SubHeader";
import SiteButtons from "../../../../components/SiteButtons";
import Container from "../../../../components/Container";
import Button from "../../../../components/elements/button";
import Input from "../../../../components/elements/form/input";
import ReusableTable from "../../../../components/ReusableTable";
import { useInventoryApi } from "../../../../store/api/hooks/inventoryApiHook";
import { useCommonApi } from "../../../../store/api/hooks/commonApiHook";
import { useAppLoader } from "../../../../_common/pageLoader/appLoaderHook";
import { useLocationsSelector } from "../../../../store/selectors/masterSelector";
import { useMenuPermissionsSelector } from "../../../../store/selectors/userSelector";
import { useDialog } from "../../../../store/api/hooks/dialogHook";
import { useLookup } from "../../../../_common/hookFunction";
import toast from "react-hot-toast";
import moment from "moment";
import Icon from "../../../../components/icons";
import { PdfGenerator, transformPurchaseOrderData } from "../../../../utils/pdfGenerator";
import { formatPhoneNumber, formatCurrency, formatQuantity, formatLocalizedDate, formatLocalizedTime, exportCsv } from "../../../../_common/functions";
import Permission from "../../../../_config/Permission";
import { useTranslation } from "react-i18next";

function PODetails() {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation("inventory");
  const navigate = useNavigate();
  const inventoryApi = useInventoryApi();
  const commonApi = useCommonApi();
  const { showLoader, hideLoader } = useAppLoader();
  const locations = useLocationsSelector();
  const { confirm } = useDialog();
  const lookup = useLookup();
  const menuPermissions = useMenuPermissionsSelector();
  
  const permissions = useMemo(() => {
    return menuPermissions?.[URLS.STOCK_CONTROL_ORDERS] || [];
  }, [menuPermissions]);
  const [purchaseOrderData, setPurchaseOrderData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [confirmationNumber, setConfirmationNumber] = useState<string>("");
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState<boolean>(false);
  const [isHeaderNoteExpanded, setIsHeaderNoteExpanded] = useState<boolean>(false);
  const [expandedNotes, setExpandedNotes] = useState<{ [key: string]: boolean }>({});
  const pdfContentRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const statusDropdownRef = useRef<HTMLDivElement>(null);

  const handleCancel = () => {
    navigate(URLS.STOCK_CONTROL_ORDERS);
  };

  const handleSaveAsCSV = () => {
    if (!purchaseOrderData?.data?.products) {
      toast.error(t("stock.poDetails.toasts.noProductData"));
      return;
    }

    try {
      const data = purchaseOrderData.data;

      // Prepare CSV headers
      const headers = [
        t("stock.poDetails.csv.poNumber"),
        t("stock.poDetails.csv.poDate"), 
        t("stock.poDetails.csv.supplier"),
        t("stock.poDetails.csv.deliveryAddress"),
        t("common.fields.productName"),
        t("common.fields.orderBoxQty"),
        t("stock.poDetails.csv.qtyPerBox"),
        t("stock.poDetails.csv.unitPrice"),
        t("stock.poDetails.csv.totalQuantity"),
        t("common.fields.totalCost"),
        t("common.fields.notes")
      ];

      // Format PO data
      const poNumber = data.orderNumber || `PO-${data.purchaseOrderId}`;
      const poDate = data.dateCreated 
        ? moment.utc(data.dateCreated).local().format("YYYY-MM-DD")
        : "";
      // if ghost vendor show N/A
      const supplier = data.fkVendorId === "NTM=" ? t("common.values.unassigned") : data.vendorName || "";
      const deliveryAddress = data.locationName || "";

      // Prepare CSV data for products
      const csvData = (data.products || []).map((product: any) => [
        `\t${poNumber}`, // Tab prefix to preserve leading zeros in Excel
        poDate,
        supplier,
        deliveryAddress,
        product.productName || "",
        product.orderBoxQuantity || 0,
        product.vendorPerBoxValue || 1,
        formatCurrency(product.boxCost || 0),
        product.orderBoxQuantity || 0, // Total Quantity same as Order Box Qty
        formatCurrency(product.totalCost || 0),
        product.note || "",
      ]);

      // Add summary rows
      csvData.push([t("stock.poDetails.csv.subTotal"), "", "", "", "", "", "", "", "", "", formatCurrency(data.subTotal || 0), ""]);
      csvData.push([t("stock.poDetails.csv.tax"), "", "", "", "", "", "", "", "", "", formatCurrency(data.tax || 0), ""]);
      csvData.push([t("stock.poDetails.csv.fees"), "", "", "", "", "", "", "", "", "", formatCurrency(data.fees || 0), ""]);
      csvData.push([t("stock.poDetails.csv.totalDue"), "", "", "", "", "", "", "", "", "", formatCurrency(data.totalDue || 0), ""]);

      // Create and download the file
      exportCsv(headers, csvData, `${poNumber}_details.csv`);

      toast.success(t("stock.poDetails.toasts.csvDownloaded"));
    } catch (error) {
      console.error("CSV generation error:", error);
      toast.error(t("stock.poDetails.toasts.csvFailed"));
    }
  };

  const handleSendPO = async (downloadOnly = true) => {
    if (!purchaseOrderData?.data) {
      toast.error(t("stock.poDetails.toasts.pdfUnavailable"));
      return;
    }

    try {
      showLoader();

      // Transform data for template
      const templateData = transformPurchaseOrderData(purchaseOrderData);

      if (downloadOnly) {
        // Just download the PDF
        await PdfGenerator.downloadPdf(templateData, undefined, i18n.language);
        hideLoader();
        toast.success(t("stock.poDetails.toasts.pdfDownloaded"));
        return;
      }

      // Show confirmation for sending email
      const vendorEmail = purchaseOrderData.data.primaryEmailAddress || "";
      const vendorName = purchaseOrderData.data.vendorName || t("stock.poDetails.fallbacks.theVendor");

      if (!vendorEmail) {
        hideLoader();
        toast.error(t("stock.poDetails.toasts.noVendorEmail"));
        return;
      }

      hideLoader();

      confirm({
        type: "warn",
        title: t("stock.poDetails.sendConfirm.title"),
        message: t("stock.poDetails.sendConfirm.message", {
          vendor: vendorName,
          email: vendorEmail,
        }),
        warning: t("stock.poDetails.sendConfirm.warning"),
        onConfirm: async () => {
          showLoader();

          // Generate PDF blob for upload and email
          const { blob } = await PdfGenerator.generatePdfBlob(templateData, i18n.language);

          // Upload PDF to server for sending email
          commonApi.uploadImage(
            { file: blob },
            (response: any) => {
              if (!response.success) {
                hideLoader();
                toast.error(t("stock.poDetails.toasts.pdfUploadFailed"));
                return;
              }

              const pdfUrl = response.data.url || "";
              console.log("PDF uploaded successfully:", pdfUrl);

              // Send email to vendor
              const emailPayload = {
                vendorName: templateData.vendorName,
                vendorEmail: vendorEmail,
                purchaseOrderDate: moment.utc(purchaseOrderData.data.dateCreated).local().format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z",
                purchaseOrderExpectedDueDate: moment.utc(purchaseOrderData.data.expectedDeliveryDate).local().format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z",
                purchaseOrderURL: pdfUrl,
                orderNumber: purchaseOrderData?.data?.purchaseOrderId ?? null,
              };

              // Call the send email API
              inventoryApi.sendEmailToVendor(
                emailPayload,
                (emailResponse: any) => {
                  hideLoader();
                  if (emailResponse.success) {
                    toast.success(emailResponse.message || t("stock.poDetails.toasts.emailSent"), {
                      duration: 5000,
                    });
                  } else {
                    toast.error(emailResponse.errorMessage || t("stock.poDetails.toasts.emailFailed"));
                  }
                },
                (emailError: any) => {
                  hideLoader();
                  toast.error(emailError?.errorMessage || emailError?.message || t("stock.poDetails.toasts.emailFailed"));
                }
              );
            },
            (error: any) => {
              hideLoader();
              console.error("PDF upload error:", error);
              toast.error(t("stock.poDetails.toasts.pdfUploadFailed"));
            }
          );
        },
        onCancel: () => {
          // User cancelled - do nothing
        },
      });
    } catch (error) {
      hideLoader();
      console.error("PDF generation error:", error);
      toast.error(t("stock.poDetails.toasts.pdfFailed"));
    }
  };

  // Helper function to get location name by ID
  const getLocationName = (locationId: any) => {
    if (!locationId || !locations) return t("common.values.notApplicable");

    // Convert locationId to string for comparison
    const searchId = locationId.toString();

    const location = locations.find((l: any) => {
      // Decode base64 locationId and compare
      try {
        const decodedId = atob(l.locationId);
        return decodedId === searchId;
      } catch (error) {
        // If decoding fails, try direct comparison
        return l.locationId === searchId;
      }
    });

    return location?.locationName || t("common.values.notApplicable");
  };

  // Helper function to truncate text
  const truncateText = (text: string, wordLimit: number = 8) => {
    const words = text.split(' ');
    if (words.length <= wordLimit) return text;
    return words.slice(0, wordLimit).join(' ');
  };

  // Helper function to toggle header note expansion
  const toggleHeaderNoteExpansion = () => {
    setIsHeaderNoteExpanded(prev => !prev);
  };

  // Helper function to toggle note expansion for table rows
  const toggleNoteExpansion = (productKey: string) => {
    setExpandedNotes(prev => ({
      ...prev,
      [productKey]: !prev[productKey]
    }));
  };

  // Handle status change
  const handleStatusChange = (statusOption: any) => {
    setShowStatusDropdown(false);
    
    if (!purchaseOrderData?.data?.purchaseOrderId || !statusOption?.lookupId) {
      toast.error(t("stock.poDetails.toasts.statusUnavailable"));
      return;
    }

    showLoader();
    
    const payload = {
      purchaseOrderId: purchaseOrderData?.data?.purchaseOrderId.toString(),
      purchaseOrderStatusId: btoa(statusOption?.lookupId?.toString())
    };


    inventoryApi.purchaseOrderStatusChange(
      payload,
      (response: any) => {
        if (response?.success) {
          toast.success(t("stock.poDetails.toasts.statusUpdated"));
          // Refresh the purchase order details to get updated status
          if (id) {
            inventoryApi.purchaseOrderDetails(
              { id: id ? atob(id) : null },
              (detailsResponse: any) => {
                if (detailsResponse?.success) {
                  setPurchaseOrderData(detailsResponse);
                  // Set confirmation number from API response
                  setConfirmationNumber(detailsResponse?.data?.confirmationNumber || "");
                }
                hideLoader();
              },
              (error: any) => {
                hideLoader();
                console.error("Failed to refresh purchase order details:", error);
              }
            );
          }
        } else {
          hideLoader();
          toast.error(response?.errorMessage || t("stock.poDetails.toasts.statusFailed"));
        }
      },
      (error: any) => {
        hideLoader();
        console.error("Status change error:", error);
        toast.error(t("stock.poDetails.toasts.statusRetry"));
      }
    );
  };

  // Handle clicks outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target as Node)
      ) {
        setShowStatusDropdown(false);
      }
    };

    if (showDropdown || showStatusDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown, showStatusDropdown]);



  useEffect(() => {
    // Load locations
    commonApi.locationSearch(
      {},
      () => {},
      (error: any) => {
        console.log("Locations load error:", error);
      }
    );
  }, []);

  useEffect(() => {
    if (id) {
      showLoader();
      setLoading(true);
      inventoryApi.purchaseOrderDetails(
        { id: id ? atob(id) : null },
        (response: any) => {
          hideLoader();
          setLoading(false);
          console.log("Purchase Order Details Response:", response);
          if (!response?.success) {
            toast.error(
              response?.errorMessage || t("stock.poDetails.toasts.fetchFailed")
            );
            setPurchaseOrderData(null);
            navigate(URLS.STOCK_CONTROL_ORDERS);
            return;
          }
          setPurchaseOrderData(response);
          // Set confirmation number from API response
          setConfirmationNumber(response?.data?.confirmationNumber || "");
        },
        (error: any) => {
          hideLoader();
          setLoading(false);
          console.error("Purchase Order Details Error:", error);
          toast.error(t("stock.poDetails.toasts.fetchFailed"));
          setPurchaseOrderData(null);
          navigate(URLS.STOCK_CONTROL_ORDERS);
        }
      );
    }
  }, [id]);

  return (
    <Permission value={permissions} check="R" page>
      <Layout>
        <Header />
        <SubHeader
          title={t("stock.poDetails.pageTitle")}
          // subTitle="Create New Receive Stock To Update Inventory Without Purchase Order"
        >
          <div className="flex space-x-2">
            <SiteButtons
              data={[
                {
                  text: t("stock.poDetails.buttons.backToList"),
                  click: handleCancel,
                },
              ]}
            />
          </div>
        </SubHeader>
        <Container>
        {!loading && purchaseOrderData?.data ? (
          <div ref={pdfContentRef} className="p-8 my-6 bg-white">
            {/* Header */}
            <div className="flex flex-wrap justify-between items-start gap-4 border-b pb-4">
              <div className="flex gap-12">
                <div>
                  <h2 className="text-lg font-semibold text-gray-800">
                    {t("common.fields.orderNumberLabel")}{" "}
                    <span className="bg-secondary text-white font-bold px-2 rounded">
                      {purchaseOrderData.data.orderNumber ||
                        `PO-${purchaseOrderData.data.purchaseOrderId}`}
                    </span>
                  </h2>
                  <p className="text-sm text-gray-700 mt-1">
                    <span className="font-medium">{t("stock.poDetails.fields.orderedBy")}</span>{" "}
                    {purchaseOrderData.data.userName ||
                      purchaseOrderData.data.displayName ||
                      t("common.values.notApplicable")}
                  </p>
                </div>

                <div className="space-y-1">
                  <Input
                    label={t("common.fields.confirmationNumber")}
                    type="text"
                    className="!my-0"
                    value={confirmationNumber}
                    placeholder={t("common.values.notAvailable")}
                    disabled={true}
                  />
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Permission value={permissions} check="W">
                  <SiteButtons
                    data={[
                      // Show "Receive Order" only if status is 177/261, isComplete is false, and not all qty received
                      ...((purchaseOrderData.data.fkPurchaseOrderStatusLookupValueId === btoa(`177`) ||
                        purchaseOrderData.data.fkPurchaseOrderStatusLookupValueId === btoa(`261`)) &&
                        !purchaseOrderData.data.isComplete &&
                        !(purchaseOrderData.data.products?.length > 0 && purchaseOrderData.data.products.every((p: any) => p.orderBoxQuantity == p.receiveQuantity))
                        ? [
                            {
                              text: t("stock.poDetails.buttons.receiveOrder"),
                              click: () => {
                                navigate(
                                  URLS.STOCK_CONTROL_PO_RECEIVE_ORDER(id || "")
                                );
                              },
                            },
                          ]
                        : []),
                      // Show "Edit PO" only if status is 178
                      ...(purchaseOrderData.data
                        .fkPurchaseOrderStatusLookupValueId === btoa(`178`)
                        ? [
                            {
                              text: t("stock.poDetails.buttons.editPO"),
                              click: () => {
                                navigate(
                                  URLS.STOCK_CONTROL_EDIT_PO(
                                    purchaseOrderData.data.purchaseOrderId,
                                    purchaseOrderData.data
                                      .fkPurchaseOrderTypeLookupValueId
                                  )
                                );
                              },
                            },
                          ]
                        : []),
                      // Show "Complete" for Pending Receive (177) and Partially Received (261)
                      ...((purchaseOrderData.data.fkPurchaseOrderStatusLookupValueId === btoa(`177`) ||
                        purchaseOrderData.data.fkPurchaseOrderStatusLookupValueId === btoa(`261`))
                        ? [
                            {
                              text: t("stock.poDetails.buttons.completeOrder"),
                              theme: "primary" as const,
                              click: () => handleStatusChange(
                                { 
                                  lookupId: 262,
                                  lookupValue: "Complete",
                                  lookupType: "62"
                                }
                              ),
                            },
                          ]
                        : []),
                      // Show "Send PO" only if status is 177, vendor is assigned, vendor has email, and email is enabled
                      ...(purchaseOrderData.data
                        .fkPurchaseOrderStatusLookupValueId === btoa(`177`) &&
                        purchaseOrderData.data.fkVendorId &&
                        purchaseOrderData.data.primaryEmailAddress &&
                        purchaseOrderData.data.isEmailEnabled &&
                        purchaseOrderData.data.fkVendorId !== "NTM="
                        ? [
                            {
                              text: t("stock.poDetails.buttons.sendPO"),
                              click: () => handleSendPO(false), // Explicitly pass false for email sending
                            },
                          ]
                        : []),
                    ]}
                  />
                </Permission>

                  {/* {purchaseOrderData.data.fkVendorId && ( */}
                  {purchaseOrderData.data.fkPurchaseOrderStatusLookupValueId == btoa(`177`) &&(
                  // <div className="relative" ref={statusDropdownRef}>
                  //   <Button onClick={() => setShowStatusDropdown(!showStatusDropdown)}>
                  //     Change Status
                  //   </Button>
                  // {showStatusDropdown && (
                  //   <div className="absolute right-0 mt-2 w-48 bg-white border rounded shadow-lg z-10">
                  //     {lookup.getPOStatus().map((status: any) => {
                  //       // Decode the current status ID and check if it matches
                  //       const currentStatusId = purchaseOrderData?.data?.fkPurchaseOrderStatusLookupValueId 
                  //         ? atob(purchaseOrderData.data.fkPurchaseOrderStatusLookupValueId) 
                  //         : null;
                  //       const isCurrentStatus = currentStatusId && status.lookupId.toString() === currentStatusId;
                        
                  //       return (
                  //         <button
                  //           key={status.lookupId}
                  //           className={`block w-full text-left px-4 py-2 text-sm ${
                  //             isCurrentStatus 
                  //               ? 'opacity-50 cursor-not-allowed bg-gray-100' 
                  //               : 'hover:bg-gray-100'
                  //           }`}
                  //           onClick={() => !isCurrentStatus && handleStatusChange(status)}
                  //           disabled={!!isCurrentStatus}
                  //         >
                  //           {status.lookupValue} {isCurrentStatus ? '(Current)' : ''}
                  //         </button>
                  //       );
                  //     })}
                  //   </div>
                  // )}
                  // </div>
                  <Permission value={permissions} check="D">
                    <Button
                      type="button"
                      onClick={() => handleStatusChange({
                        "lookupId": 263,
                        "lookupValue": "Cancel",
                        "lookupType": "62"
                      })}
                    >
                      {t("stock.poDetails.buttons.cancelOrder")}
                    </Button>
                  </Permission>
                )}

                <div className="relative" ref={dropdownRef}>
                  <Button onClick={() => setShowDropdown(!showDropdown)}>
                    <Icon name="three-dots-vertical" />
                  </Button>
                  {showDropdown && (
                    <div className="absolute right-0 mt-2 w-36 bg-white border rounded shadow-lg z-10">
                      <button
                        className="opacity-30 cursor-not-allowed block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                        onClick={() => {
                          setShowDropdown(false);
                          /* TODO: Implement duplicate functionality */
                        }}
                      >
                        {t("stock.poDetails.buttons.duplicate")}
                      </button>
                      <button
                        className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                        onClick={() => {
                          setShowDropdown(false);
                          handleSendPO(true); // Pass downloadOnly = true
                        }}
                      >
                        {t("stock.poDetails.buttons.saveAsPdf")}
                      </button>
                      <button
                        className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                        onClick={() => {
                          setShowDropdown(false);
                          handleSaveAsCSV();
                        }}
                      >
                        {t("stock.poDetails.buttons.saveAsCsv")}
                      </button>
                      <button
                        className="opacity-30 cursor-not-allowed block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                        onClick={() => {
                          setShowDropdown(false);
                          /* TODO: Implement cancel order functionality */
                        }}
                      >
                        {t("stock.poDetails.buttons.cancelOrder")}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Vendor Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4 text-sm text-gray-700">
              <div className="space-y-4">
                <p>
                  <span className="font-semibold">{t("stock.poDetails.fields.vendorName")}</span>{" "}
                  {purchaseOrderData.data.vendorCode === "GhostVendor" ? t("common.values.unassigned") : purchaseOrderData.data.vendorName || t("common.values.notApplicable")}
                </p>
                <p>
                  <span className="font-semibold">{t("stock.poDetails.fields.vendorPhone")}</span>{" "}
                  {purchaseOrderData?.data?.primaryPhone ? formatPhoneNumber(purchaseOrderData.data.primaryPhone) : t("common.values.notApplicable")}
                </p>
                <p>
                  <span className="font-semibold">{t("stock.poDetails.fields.vendorEmail")}</span>{" "}
                  {purchaseOrderData.data.primaryEmailAddress || t("common.values.notApplicable")}
                </p>
                <p>
                  <span className="font-semibold">{t("stock.poDetails.fields.orderDate")}</span>{" "}
                  {purchaseOrderData.data.dateCreated
                    ? moment
                        .utc(purchaseOrderData.data.dateCreated)
                        .local()
                        .format("MM/DD/YYYY")
                    : t("common.values.notApplicable")}
                </p>
                <p>
                  <span className="font-semibold">{t("stock.poDetails.fields.orderTime")}</span>{" "}
                  {purchaseOrderData.data.dateCreated
                    ? formatLocalizedTime(moment.utc(purchaseOrderData.data.dateCreated).local().toDate(), i18n.language)
                    : t("common.values.notApplicable")}
                </p>
              </div>

              <div className="space-y-4">
                <p>
                  <span className="font-semibold">{t("common.fields.shipTo")}</span>{" "}
                  {purchaseOrderData.data.locationName || t("common.values.notAvailable")}
                </p>
                <p>
                  <span className="font-semibold">{t("stock.poDetails.fields.expectedDelivery")}</span>{" "}
                  {purchaseOrderData.data.expectedDeliveryDate
                    ? `${formatLocalizedDate(moment.utc(purchaseOrderData.data?.expectedDeliveryDate).local().toDate(), i18n.language, { month: "2-digit", day: "2-digit", year: "numeric" })} ${formatLocalizedTime(moment.utc(purchaseOrderData.data?.expectedDeliveryDate).local().toDate(), i18n.language)}`
                    : t("common.values.notApplicable")}
                </p>

                <div>
                  <span className="font-semibold">{t("common.fields.noteLabel")}</span>{" "}
                  {purchaseOrderData.data.note ? (
                    <span className="text-gray-700">
                      {purchaseOrderData.data.note.split(' ').length > 8 && !isHeaderNoteExpanded
                        ? truncateText(purchaseOrderData.data.note, 8)
                        : purchaseOrderData.data.note}
                      {purchaseOrderData.data.note.split(' ').length > 8 && (
                        <>
                          {!isHeaderNoteExpanded && "..."}
                          <button
                            onClick={toggleHeaderNoteExpansion}
                            className="ml-1 text-blue-600 hover:text-blue-800 underline text-xs"
                          >
                            {t(isHeaderNoteExpanded ? "common.actions.showLess" : "common.actions.showMore")}
                          </button>
                        </>
                      )}
                    </span>
                  ) : (
                    "-"
                  )}
                </div>
                <p className="flex items-center gap-2">
                  <span className="font-semibold">{t("stock.poDetails.fields.vendorWebsite")}</span>
                  {purchaseOrderData.data.website ? (
                    <Button
                      onClick={() => {
                        let url = purchaseOrderData.data.website;
                        if (!url.startsWith('http://') && !url.startsWith('https://')) {
                          url = 'https://' + url;
                        }
                        window.open(url, "_blank");
                      }}
                    >
                      {t("stock.poDetails.buttons.goToVendorSite")}
                    </Button>
                  ) : (
                    <Button disabled>{t("stock.poDetails.buttons.goToVendorSite")}</Button>
                  )}
                </p>
              </div>
            </div>

            {/* Products Info */}
            <div className="mt-8">
              <h3 className="font-semibold text-gray-800 mb-3 border-b pb-1">
                {t("common.sections.productsInfo")}
              </h3>

              <ReusableTable
                data={(purchaseOrderData?.data?.products || []).map(
                  (product: any, index: number) => ({
                    ...product,
                    id:
                      product.purchaseOrderDetailId ||
                      product.productId ||
                      index,
                  })
                )}
                enableDrag={false}
                isRearranging={false}
                enableExpand={false}
                columns={[
                  {
                    key: "productName",
                    label: t("common.fields.productName"),
                    sortable: false,
                    render: (product: any) => (
                      <div>
                        <div className="font-semibold">
                          {product.productName}
                        </div>
                        {/* <div className="text-xs text-gray-500">
                          {product.fkProductVariantDetailId
                            ? "Variant Product"
                            : "Standard Product"}
                        </div> */}
                      </div>
                    ),
                  },
                  {
                    key: "orderBoxQuantity",
                    label: t("common.fields.orderBoxQty"),
                    sortable: false,
                    render: (product: any) => (
                      <span>{formatQuantity(product.orderBoxQuantity || 0)}</span>
                    ),
                  },
                  {
                    key: "vendorPerBoxValue",
                    label: t("common.fields.qtyWtPerBox"),
                    sortable: false,
                    render: (product: any) => (
                      <span>{formatQuantity(product.vendorPerBoxValue || 1)}</span>
                    ),
                  },
                  {
                    key: "boxCost",
                    label: t("common.fields.boxCost"),
                    sortable: false,
                    render: (product: any) => (
                      <span>${formatCurrency(product.boxCost || 0)}</span>
                    ),
                  },
                  {
                    key: "totalCost",
                    label: t("common.fields.totalCost"),
                    sortable: false,
                    render: (product: any) => (
                      <span className="font-semibold">
                        ${formatCurrency(product.totalCost || 0)}
                      </span>
                    ),
                  },
                  {
                    key: "note",
                    label: t("common.fields.note"),
                    sortable: false,
                    render: (product: any) => {
                      const noteText = product.note || "";
                      const productKey = product.purchaseOrderDetailId || product.productId || product.id;
                      const isExpanded = expandedNotes[productKey] || false;
                      const shouldTruncate = noteText && noteText.split(' ').length > 5;

                      return (
                        <div className="max-w-32">
                          {noteText ? (
                            <div className="text-sm text-gray-700">
                              <div className="break-words">
                                {shouldTruncate && !isExpanded
                                  ? truncateText(noteText, 5)
                                  : noteText}
                                {shouldTruncate && (
                                  <>
                                    {!isExpanded && "..."}
                                    <button
                                      onClick={() => toggleNoteExpansion(productKey)}
                                      className="ml-1 text-blue-600 hover:text-blue-800 underline text-xs"
                                    >
                                      {t(isExpanded ? "common.actions.showLess" : "common.actions.showMore")}
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="text-xs text-gray-400">
                              -
                            </div>
                          )}
                        </div>
                      );
                    },
                  },
                ]}
              />

              {/* Summary */}
              <div className="mt-6 border-t pt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                  <div></div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="font-semibold">{t("common.fields.subTotal")}</span>
                      <span>
                        ${formatCurrency(purchaseOrderData.data.subTotal || 0)}
                      </span>
                    </div>
                    {/* <div className="flex justify-between">
                      <span className="font-semibold">Tax:</span>
                      <span>
                        ${(purchaseOrderData.data.tax || 0).toFixed(2)}
                      </span>
                    </div> */}
                    <div className="flex justify-between">
                      <span className="font-semibold">{t("common.fields.fees")}</span>
                      <span>
                        ${formatCurrency(purchaseOrderData.data.fees || 0)}
                      </span>
                    </div>
                    <div className="flex justify-between border-t pt-2">
                      <span className="font-bold">{t("common.fields.totalDue")}</span>
                      <span className="font-bold">
                        ${formatCurrency(purchaseOrderData.data.totalDue || 0)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : !loading ? (
          <div className="flex justify-center items-center py-8">
            <p>{t("common.messages.noPurchaseOrderData")}</p>
          </div>
        ) : null}
      </Container>
    </Layout>
    </Permission>
  );
}

export default PODetails;
